import React, { useCallback, useEffect, useState } from 'react'
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableRow,
  TableCell,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
} from '@mui/material'

type Answer = 'YES' | 'NO' | 'DK' | ''
type Props = {
  questionnaire: any
  initialAnswers?: Record<string, Answer>
  initialComments?: Record<string, string>
  onChange: (data: {
    answers: Record<string, Answer>
    comments: Record<string, string>
  }) => void
}

const MedicalHistoryForm = ({
  questionnaire,
  onChange,
  initialAnswers = {},
  initialComments = {},
}: Props) => {
  const [answers, setAnswers] = useState<Record<string, Answer>>(initialAnswers)
  const [comments, setComments] =
    useState<Record<string, string>>(initialComments)

  const initializeFormData = useCallback(() => {
    setAnswers(initialAnswers ?? {})
    setComments(initialComments ?? {})
  }, [initialAnswers, initialComments])

  useEffect(() => {
    initializeFormData()
  }, [initializeFormData])

  useEffect(() => {
    onChange({ answers, comments })
  }, [answers, comments, onChange])

  const handleAnswerChange = (questionId: string, value: Answer) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }))
  }

  const handleCommentChange = (sectionId: string, value: string) => {
    setComments((prev) => ({ ...prev, [sectionId]: value }))
  }

  const renderYesNo = (id: string) => (
    <RadioGroup
      row
      value={answers[id] || ''}
      onChange={(e) => handleAnswerChange(id, e.target.value as Answer)}
    >
      <FormControlLabel
        value='YES'
        control={<Radio size='small' />}
        label='Yes'
      />
      <FormControlLabel
        value='NO'
        control={<Radio size='small' />}
        label='No'
      />
    </RadioGroup>
  )

  const renderYesNoDK = (id: string) => (
    <RadioGroup
      row
      value={answers[id] || ''}
      onChange={(e) => handleAnswerChange(id, e.target.value as Answer)}
    >
      <FormControlLabel
        value='YES'
        control={<Radio size='small' />}
        label='Yes'
      />
      <FormControlLabel
        value='NO'
        control={<Radio size='small' />}
        label='No'
      />
      <FormControlLabel
        value='DK'
        control={<Radio size='small' />}
        label="Don't Know"
      />
    </RadioGroup>
  )

  return (
    <Box>
      {questionnaire?.item?.map((section: any) => (
        <Box key={section?.linkId}>
          <Typography fontSize={12} fontWeight={600} mt={2}>
            {section?.text}
          </Typography>

          <Table size='small'>
            <TableBody>
              {section?.item?.map((q: any) => (
                <TableRow key={q?.linkId}>
                  <TableCell sx={{ fontSize: 11 }}>{q?.text}</TableCell>
                  <TableCell align='right'>
                    {section?.linkId === 'sectionA'
                      ? renderYesNo(q?.linkId)
                      : renderYesNoDK(q?.linkId)}
                  </TableCell>
                </TableRow>
              ))}

              <TableRow>
                <TableCell colSpan={2}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    label={'Comments'}
                    value={comments?.[section?.linkId] || ''}
                    onChange={(e) =>
                      handleCommentChange(section?.linkId, e.target.value)
                    }
                  />
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Box>
      ))}
    </Box>
  )
}

export default MedicalHistoryForm
